import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import confetti from "canvas-confetti";
import { useEffect, useRef } from "react";
import { getPixelCodeSnippets } from "~/components/instructions/pixel-instructions";
import { PageLayout } from "~/components/page-layout";
import {
  isFeatureFlagEnabled,
  useFeatureFlag,
} from "~/instrumentation/feature-flags";
import { trpc } from "~/lib/trpc";
import { QuickstartSection } from "./-quickstart/quickstart";

export const Route = createFileRoute("/(app)/")({
  component: App,
  beforeLoad: async () => {
    if (!isFeatureFlagEnabled("quickstart")) {
      throw redirect({ to: "/pages", replace: true });
    }
  },
  loader: async ({ context }) => {
    const scriptContent = await context.queryClient.ensureQueryData(
      trpc.installation.getInstallationScript.queryOptions(),
    );

    const [pixelCodeSnippets, _] = await Promise.all([
      getPixelCodeSnippets(scriptContent),
      context.queryClient.ensureQueryData(
        trpc.quickstart.getQuickstartStatus.queryOptions(),
      ),
    ]);

    return {
      crumb: {
        id: "home",
        label: "Quickstart",
        options: [],
      },
      pixel: {
        scriptContent,
        ...pixelCodeSnippets,
      },
    };
  },
});

function App() {
  const isQuickstartEnabled = useFeatureFlag("quickstart");

  const { data: quickstartStatus } = useSuspenseQuery(
    trpc.quickstart.getQuickstartStatus.queryOptions(),
  );

  const prevIsCompleteRef = useRef(quickstartStatus.isComplete);

  const fireConfetti = (origin: { x: number; y: number }) => {
    confetti({
      origin,
      particleCount: 60,
      spread: 360,
      gravity: 0.9,
      decay: 0.95,
      startVelocity: 30,
    });
  };

  useEffect(() => {
    // Only fire confetti when transitioning from false to true
    if (quickstartStatus.isComplete && !prevIsCompleteRef.current) {
      setTimeout(() => {
        fireConfetti({ x: 0.5, y: 0.4 });
      }, 200);
      setTimeout(() => {
        fireConfetti({ x: 0.4, y: 0.2 });
      }, 300);
      setTimeout(() => {
        fireConfetti({ x: 0.7, y: 0.3 });
      }, 400);
    }
    prevIsCompleteRef.current = quickstartStatus.isComplete;
  }, [quickstartStatus.isComplete]);

  if (isQuickstartEnabled) {
    return (
      <PageLayout.Main>
        <div className="w-full xl:max-w-screen-xl mx-auto gap-4 flex flex-col items-center mb-8">
          <h1 className="text-xl md:text-2xl font-bold mx-auto">
            Let's Get Started!
          </h1>
          <p className="text-muted-foreground mx-auto md:w-100 w-80 text-center text-xs md:text-sm mb-4">
            We'll show you how to create your first personalized experience for
            your website visitors.
          </p>
          <QuickstartSection quickstartStatus={quickstartStatus} />
        </div>
      </PageLayout.Main>
    );
  }
  return null;
}
